import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useCallback, useState, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import CommonTable from "../../../common_components/CommonTable";
import CommonLoader from "../../../common_components/CommonLoader";
import { Dropdown } from "../../../common_components/FormInputs";
import TableToolbar from "../../../common_components/TableToolbar";
import ConfirmActionModal from "../../../common_components/ConfirmActionModal";
import { fetchHubspotUsers, assignPortalUser, selectHubspotOwners, selectHubspotPortalUsers, selectHubspotUsersLoading, selectHubspotUsersError, selectHubspotUsersSaving, } from "../../../redux/integrationCredentialsSlice";
const COLUMNS = [
    { id: "hubspot_username", label: "Hubspot Username", width: "280px", align: "left", sortable: true },
    { id: "portal_user", label: "Portal User", width: "340px", align: "left", sortable: true },
    { id: "assign_date", label: "Assign Date", width: "220px", align: "left", sortable: true },
];
const NA = _jsx(Typography, { sx: { fontSize: 12, color: "#9CA3AF" }, children: "N/A" });
const DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
});
function formatDate(date) {
    if (!date)
        return "";
    const d = new Date(date);
    if (Number.isNaN(d.getTime()))
        return "";
    const parts = DATE_FORMATTER.formatToParts(d);
    const get = (type) => parts.find((p) => p.type === type)?.value || "";
    return `${get("month")}/${get("day")}/${get("year")} ${get("hour")}:${get("minute")} ${get("dayPeriod")}`;
}
function getAssignedUserId(row) {
    if (row.user_id && typeof row.user_id === "object")
        return row.user_id._id || "";
    return row.user_id || "";
}
function getAssignedUserLabel(row) {
    const u = row.user_id;
    if (u && typeof u === "object") {
        const au = u;
        const name = `${au.first_name || ""} ${au.last_name || ""}`.trim();
        return name || au.email || "";
    }
    return "";
}
const HubspotUsersPanel = () => {
    const dispatch = useDispatch();
    const owners = useSelector(selectHubspotOwners);
    const portalUsers = useSelector(selectHubspotPortalUsers);
    const loading = useSelector(selectHubspotUsersLoading);
    const error = useSelector(selectHubspotUsersError);
    const saving = useSelector(selectHubspotUsersSaving);
    const [searchText, setSearchText] = useState("");
    const [sortConfig, setSortConfig] = useState({ columnId: null, direction: "asc" });
    const [removeState, setRemoveState] = useState({ open: false, owner: null });
    const [hiddenColumns, setHiddenColumns] = useState(new Set());
    const [columnVisibilityAnchor, setColumnVisibilityAnchor] = useState(null);
    const [columnOrder, setColumnOrder] = useState(null);
    const [pinnedColumns, setPinnedColumns] = useState({ left: [], right: [] });
    const tableWrapperRef = useRef(null);
    useEffect(() => {
        dispatch(fetchHubspotUsers());
    }, [dispatch]);
    // Close any open portaled dropdown when the table area scrolls — otherwise
    // the menu (position: fixed at open-time) becomes detached from its trigger.
    useEffect(() => {
        const wrapper = tableWrapperRef.current;
        if (!wrapper)
            return;
        const handler = () => {
            document.body.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
        };
        wrapper.addEventListener("scroll", handler, true);
        return () => wrapper.removeEventListener("scroll", handler, true);
    }, [loading]);
    const portalUserOptions = useMemo(() => portalUsers.map((u) => ({
        label: `${u.first_name} ${u.last_name} (${u.email})`,
        value: u._id,
    })), [portalUsers]);
    const doAssign = useCallback((owner, userId) => {
        dispatch(assignPortalUser({
            hubspot_owner_id: owner.hubspot_owner_id,
            hubspot_user_id: owner.hubspot_user_id,
            hubspot_username: owner.hubspot_username,
            user_id: userId,
        })).then(() => dispatch(fetchHubspotUsers()));
    }, [dispatch]);
    const handleDropdownChange = useCallback((owner, newUserId) => {
        const currentId = getAssignedUserId(owner);
        // Removal (was assigned, now cleared) → confirm
        if (currentId && !newUserId) {
            setRemoveState({ open: true, owner });
            return;
        }
        doAssign(owner, newUserId || null);
    }, [doAssign]);
    const handleConfirmRemove = useCallback(() => {
        if (!removeState.owner)
            return;
        doAssign(removeState.owner, null);
        setRemoveState({ open: false, owner: null });
    }, [removeState.owner, doAssign]);
    const filteredAndSortedRows = useMemo(() => {
        const term = searchText.trim().toLowerCase();
        let list = owners.map((o) => ({ ...o, id: o.hubspot_owner_id }));
        if (term) {
            list = list.filter((o) => {
                const name = (o.hubspot_username || "").toLowerCase();
                const email = (o.hubspot_email || "").toLowerCase();
                const portalLabel = getAssignedUserLabel(o).toLowerCase();
                return name.includes(term) || email.includes(term) || portalLabel.includes(term);
            });
        }
        if (sortConfig.columnId) {
            const dir = sortConfig.direction === "asc" ? 1 : -1;
            const getSortVal = (row) => {
                switch (sortConfig.columnId) {
                    case "hubspot_username":
                        return (row.hubspot_username || row.hubspot_email || "").toLowerCase();
                    case "portal_user":
                        return getAssignedUserLabel(row).toLowerCase();
                    case "assign_date":
                        return row.assign_date ? new Date(row.assign_date).getTime() : 0;
                    default:
                        return "";
                }
            };
            list = [...list].sort((a, b) => {
                const va = getSortVal(a);
                const vb = getSortVal(b);
                if (va < vb)
                    return -1 * dir;
                if (va > vb)
                    return 1 * dir;
                return 0;
            });
        }
        return list;
    }, [owners, searchText, sortConfig]);
    const handleSortChange = useCallback((columnId, direction) => {
        setSortConfig({ columnId: columnId, direction });
    }, []);
    if (error) {
        return (_jsx(Box, { sx: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 4 }, children: _jsxs(Box, { sx: { textAlign: "center", maxWidth: 480 }, children: [_jsx(Typography, { sx: { fontSize: 14, fontWeight: 600, color: "#ef4444", mb: 1 }, children: "HubSpot Not Configured" }), _jsx(Typography, { sx: { fontSize: 13, color: "#6b7280", lineHeight: 1.6 }, children: error }), _jsxs(Typography, { sx: { fontSize: 12, color: "#9ca3af", mt: 1.5 }, children: ["Set the ", _jsx("strong", { children: "HUBSPOT_ACCESS_TOKEN" }), " environment variable on the server."] })] }) }));
    }
    const renderCell = (row, column) => {
        if (column.id === "hubspot_username") {
            const name = row.hubspot_username?.trim();
            if (!name && !row.hubspot_email)
                return NA;
            return (_jsxs(Box, { children: [name ? (_jsxs(Typography, { sx: { fontSize: "14px !important", fontWeight: 500, color: "#111827", lineHeight: 1.4 }, children: [name, row.hubspot_user_id && (_jsxs(Typography, { component: "span", sx: { fontSize: "14px !important", fontWeight: 400, color: "#6b7280", ml: "4px" }, children: ["(", row.hubspot_user_id, ")"] }))] })) : (_jsx(Typography, { sx: { fontSize: "14px !important", color: "#9CA3AF" }, children: "N/A" })), row.hubspot_email && (_jsx(Typography, { sx: { fontSize: "12px !important", color: "#6b7280", mt: "2px" }, children: row.hubspot_email }))] }));
        }
        if (column.id === "portal_user") {
            const assignedId = getAssignedUserId(row);
            const isSaving = saving === row.hubspot_owner_id;
            return (_jsx(Box, { sx: { width: "100%", maxWidth: 320, pointerEvents: isSaving ? "none" : "auto", opacity: isSaving ? 0.5 : 1 }, children: _jsx(Dropdown, { value: assignedId, options: portalUserOptions, onChange: (val) => handleDropdownChange(row, val), placeholder: "Select Portal User", searchable: true, clearable: true, usePortal: true, size: "sm", fullWidth: true }) }));
        }
        if (column.id === "assign_date") {
            const isSaving = saving === row.hubspot_owner_id;
            if (isSaving) {
                return (_jsx(Typography, { sx: { fontSize: 12, color: "#9ca3af", whiteSpace: "nowrap" }, children: "Saving\u2026" }));
            }
            if (!row.assign_date)
                return NA;
            return (_jsx(Typography, { sx: { fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }, children: formatDate(row.assign_date) }));
        }
        return null;
    };
    const removeOwnerName = removeState.owner?.hubspot_username?.trim() || removeState.owner?.hubspot_email || "this user";
    const removePortalName = removeState.owner ? getAssignedUserLabel(removeState.owner) : "";
    return (_jsxs(Box, { className: "hubspot-users-panel", children: [loading ? (_jsx(Box, { className: "hubspot-users-panel-loader", children: _jsx(CommonLoader, { className: "table-loader" }) })) : (_jsxs(_Fragment, { children: [_jsx(Box, { className: "hubspot-users-panel-toolbar", children: _jsx(TableToolbar, { title: "", showSearch: true, showFullScreen: false, searchValue: searchText, onSearchChange: setSearchText, searchPlaceholder: "Search by name, email, or portal user", enableColumnVisibility: true, onColumnVisibilityClick: (e) => setColumnVisibilityAnchor(e.currentTarget) }) }), _jsx(Box, { className: "hubspot-users-panel-table-wrapper", ref: tableWrapperRef, children: _jsx(CommonTable, { columns: COLUMNS, rows: filteredAndSortedRows, renderCell: renderCell, expandTrigger: "none", sortConfig: sortConfig.columnId ? sortConfig : undefined, onSortChange: handleSortChange, columnVisibilityConfig: {
                                enable: true,
                                columns: ["hubspot_username", "portal_user", "assign_date"],
                                enableReorder: true,
                            }, hiddenColumns: hiddenColumns, onColumnVisibilityChange: setHiddenColumns, columnVisibilityAnchorEl: columnVisibilityAnchor, onColumnVisibilityClose: () => setColumnVisibilityAnchor(null), enableColumnActions: true, enableColumnReorder: true, columnOrder: columnOrder, onColumnOrderChange: setColumnOrder, enableColumnPinning: true, pinnedColumns: pinnedColumns, onColumnPinningChange: setPinnedColumns, stickyHeader: true, maxHeight: "100%", emptyMessage: searchText ? "No HubSpot owners match your search" : "No HubSpot owners found" }) })] })), removeState.open && (_jsx(ConfirmActionModal, { className: "delete-popup-container", open: removeState.open, loading: saving === removeState.owner?.hubspot_owner_id, title: "Remove Portal User?", description: `You're about to unassign ${removePortalName ? `"${removePortalName}"` : "the portal user"} from ${removeOwnerName}. This action can be reversed by re-assigning.`, confirmText: "Yes, Remove", cancelText: "No, Cancel", onClose: () => setRemoveState({ open: false, owner: null }), onConfirm: handleConfirmRemove }))] }));
};
export default HubspotUsersPanel;
